/**
 * 💜 CRUSHES TAB - Mode Mozart/Jobs/Tesla
 * 
 * Affiche les personnes qui ont un crush sur l'utilisateur
 * ✅ Intégration Paywall : Les utilisateurs gratuits voient des profils floutés
 * ✅ Les utilisateurs Premium/VIP voient les identités complètes
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Sparkles, Lock, Crown, Zap, Eye } from 'lucide-react';

function CrushesTab({ receivedCrushes = [], isDarkMode, isPremium = false, onShowPaywall }) {
  const safeCrushes = Array.isArray(receivedCrushes) ? receivedCrushes : [];

  if (safeCrushes.length === 0) {
    return (
      <div className="text-center py-12">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
        >
          <Sparkles className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
          <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            🥺 Personne ne t'a encore liké...
          </h3>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
            Mais ça va venir, t'es irrésistible ! 😏✨
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div>
      {/* Header avec compteur */}
      <motion.div 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className={`p-4 rounded-xl mb-4 ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}
      >
        <p className={`text-center ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
          💜 <strong>{safeCrushes.length}</strong> personne(s) ont un crush sur toi !
        </p>
        {isPremium ? (
          <p className={`text-center text-sm mt-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
            <Crown className="w-4 h-4 inline mr-1" />
            Tu es Premium ! Clique sur un profil pour le découvrir 👀
          </p>
        ) : (
          <p className={`text-center text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <Lock className="w-4 h-4 inline mr-1" />
            Passe Premium pour voir qui c'est !
          </p>
        )}
      </motion.div>

      {/* 💎 Banner Premium si pas abonné */}
      {!isPremium && safeCrushes.length > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-4 p-4 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Eye className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h4 className="font-bold">Quelqu'un te kiffe ! 💕</h4>
              <p className="text-sm text-white/80">
                Découvre qui avec Premium
              </p>
            </div>
            <motion.button
              onClick={() => onShowPaywall?.({ feature: 'seeWhoLikesYou' })}
              className="px-4 py-2 rounded-full bg-white text-purple-600 font-bold text-sm hover:bg-white/90 transition-colors flex items-center gap-1"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="crushes-premium-cta"
            >
              <Zap className="w-4 h-4" />
              Voir
            </motion.button>
          </div>
        </motion.div>
      )}

      {/* Liste des crushes */}
      <div className="space-y-3">
        {safeCrushes.map((crush, idx) => (
          <motion.div
            key={crush.id || idx}
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: idx * 0.1 }}
            className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} ${isPremium ? 'cursor-pointer hover:bg-gray-700/50' : ''}`}
            whileHover={{ scale: 1.02 }}
            onClick={() => {
              if (!isPremium && onShowPaywall) {
                onShowPaywall({ feature: 'see_who_liked' });
              }
              // TODO: Si premium, ouvrir le profil du crush
            }}
          >
            <div className="flex items-center gap-4">
              {/* Avatar - flouté si pas premium */}
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center overflow-hidden">
                  {crush.avatar ? (
                    <img 
                      src={crush.avatar} 
                      alt="" 
                      className={`w-full h-full object-cover ${isPremium ? '' : 'blur-lg'}`}
                    />
                  ) : (
                    <span className="text-2xl">{isPremium ? '👤' : '🔒'}</span>
                  )}
                </div>
                {!isPremium && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-white drop-shadow-lg" />
                  </div>
                )}
                {isPremium && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                    <Eye className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1">
                {isPremium ? (
                  <>
                    <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {crush.displayName || crush.firstName || 'Crush #' + (idx + 1)}
                    </h4>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {crush.bio?.substring(0, 50) || 'A un crush sur toi !'}
                    </p>
                  </>
                ) : (
                  <>
                    <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Crush mystère #{idx + 1}
                    </h4>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {crush.hint || 'Passe Premium pour voir son profil !'}
                    </p>
                  </>
                )}
                {crush.commonInterests?.length > 0 && (
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {crush.commonInterests.slice(0, 3).map((interest, i) => (
                      <span 
                        key={i}
                        className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400"
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Action */}
              {isPremium ? (
                <motion.button
                  className="px-3 py-1.5 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-bold"
                  whileHover={{ scale: 1.1 }}
                >
                  Voir
                </motion.button>
              ) : (
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <Heart className="w-6 h-6 text-pink-500 fill-pink-500" />
                </motion.div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Call to action final */}
      <motion.div 
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className={`mt-6 p-4 rounded-xl text-center ${isDarkMode ? 'bg-gradient-to-r from-purple-900/50 to-pink-900/50' : 'bg-gradient-to-r from-purple-50 to-pink-50'}`}
      >
        {isPremium ? (
          <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
            <Crown className="w-4 h-4 inline mr-1" />
            <strong>Premium activé !</strong> Tu vois qui te kiffe 💕
          </p>
        ) : (
          <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            💡 <strong>Astuce :</strong> Avec Premium, découvre instantanément qui t'a liké !
          </p>
        )}
      </motion.div>
    </div>
  );
}

export default CrushesTab;
